import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  TicketIcon,
  ClockIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  StarIcon,
  HeartIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import {
  QuestionMarkCircleIcon as QuestionMarkCircleIconSolid,
  TicketIcon as TicketIconSolid,
  CheckCircleIcon as CheckCircleIconSolid
} from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const SupportDashboard = () => {
  // eslint-disable-next-line no-unused-vars
  const [searchQuery, setSearchQuery] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Mock data for support statistics
  const supportStats = [
    {
      title: 'Open Tickets',
      value: '12',
      change: '+3 from yesterday',
      trend: 'up',
      icon: TicketIcon,
      iconSolid: TicketIconSolid,
      gradient: 'from-red-500 to-pink-600',
      bgGradient: 'from-red-50 to-pink-50',
      borderColor: 'border-red-200'
    },
    {
      title: 'Resolved Today',
      value: '8',
      change: '+2 from yesterday',
      trend: 'up',
      icon: CheckCircleIcon,
      iconSolid: CheckCircleIconSolid,
      gradient: 'from-green-500 to-emerald-600',
      bgGradient: 'from-green-50 to-emerald-50',
      borderColor: 'border-green-200'
    },
    {
      title: 'Avg Response Time',
      value: '1.2h',
      change: '-15 min improvement',
      trend: 'down',
      icon: ClockIcon,
      gradient: 'from-blue-500 to-cyan-600',
      bgGradient: 'from-blue-50 to-cyan-50',
      borderColor: 'border-blue-200'
    },
    {
      title: 'Satisfaction Score',
      value: '4.8/5',
      change: '+0.2 this week',
      trend: 'up',
      icon: StarIcon,
      gradient: 'from-yellow-500 to-orange-600',
      bgGradient: 'from-yellow-50 to-orange-50',
      borderColor: 'border-yellow-200'
    }
  ];

  // Recent tickets data
  const recentTickets = [
    {
      id: 'TK-2024-001',
      title: 'Unable to access payroll portal',
      priority: 'High',
      status: 'In Progress',
      category: 'Technical',
      created: '2 hours ago',
      assignedTo: 'Sarah Tech',
      description: 'Employee unable to log into payroll system after password reset'
    },
    {
      id: 'TK-2024-002',
      title: 'Request for leave policy clarification',
      priority: 'Medium',
      status: 'Pending',
      category: 'HR Policy',
      created: '4 hours ago',
      assignedTo: 'John HR',
      description: 'Need clarification on maternal leave policy duration'
    },
    {
      id: 'TK-2024-003',
      title: 'Training certificate not generated',
      priority: 'Medium',
      status: 'Resolved',
      category: 'Training',
      created: '1 day ago',
      assignedTo: 'Lisa Training',
      description: 'Certificate for completed safety training not available for download'
    },
    {
      id: 'TK-2024-004',
      title: 'Office equipment replacement request',
      priority: 'Low',
      status: 'Open',
      category: 'Facilities',
      created: '2 days ago',
      assignedTo: 'Mike Facilities',
      description: 'Request for ergonomic chair replacement due to back pain'
    }
  ];

  // Knowledge base articles
  const popularArticles = [
    {
      id: 1,
      title: 'How to Reset Your Password',
      category: 'Account Access',
      views: 1250,
      helpful: 95,
      lastUpdated: '2 days ago'
    },
    {
      id: 2,
      title: 'Leave Application Process',
      category: 'HR Policies',
      views: 890,
      helpful: 87,
      lastUpdated: '1 week ago'
    },
    {
      id: 3,
      title: 'IT Equipment Request Guidelines',
      category: 'IT Support',
      views: 650,
      helpful: 92,
      lastUpdated: '3 days ago'
    },
    {
      id: 4,
      title: 'Training Course Enrollment',
      category: 'Learning',
      views: 580,
      helpful: 89,
      lastUpdated: '5 days ago'
    }
  ];

  // Quick actions
  const quickActions = [
    {
      title: 'Create New Ticket',
      description: 'Submit a support request',
      icon: TicketIcon,
      link: '/support/tickets/create',
      color: 'apollo-primary',
      bgColor: 'bg-apollo-primary/10',
      hoverColor: 'hover:bg-apollo-primary/20'
    },
    {
      title: 'Live Chat',
      description: 'Chat with support agent',
      icon: ChatBubbleLeftRightIcon,
      link: '/support/chat',
      color: 'green-600',
      bgColor: 'bg-green-100',
      hoverColor: 'hover:bg-green-200'
    },
    {
      title: 'Knowledge Base',
      description: 'Browse help articles',
      icon: DocumentTextIcon,
      link: '/support/knowledge-base',
      color: 'blue-600',
      bgColor: 'bg-blue-100',
      hoverColor: 'hover:bg-blue-200'
    },
    {
      title: 'Contact Support',
      description: 'Get in touch directly',
      icon: PhoneIcon,
      link: '/support/contact',
      color: 'purple-600',
      bgColor: 'bg-purple-100',
      hoverColor: 'hover:bg-purple-200'
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'text-red-600 bg-red-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'text-blue-600 bg-blue-100';
      case 'in progress':
        return 'text-yellow-600 bg-yellow-100';
      case 'pending':
        return 'text-orange-600 bg-orange-100';
      case 'resolved':
        return 'text-green-600 bg-green-100';
      case 'closed':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-apollo-gray">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 flex items-center">
                  <QuestionMarkCircleIconSolid className="w-6 h-6 sm:w-8 sm:h-8 text-apollo-primary mr-2 sm:mr-3" />
                  Support Center
                </h1>
                <p className="mt-1 sm:mt-2 text-gray-600 text-sm sm:text-base">
                  Get help and support for all your workplace needs
                </p>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                <Link
                  to="/support/tickets/create"
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-apollo-primary text-white rounded-lg hover:bg-apollo-secondary transition-colors font-medium text-sm sm:text-base"
                >
                  <TicketIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  New Ticket
                </Link>
                <Link
                  to="/support/chat"
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm sm:text-base"
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Live Chat
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Support Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {supportStats.map((stat, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${stat.bgGradient} rounded-xl p-4 sm:p-6 border ${stat.borderColor} hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}
            >
              <div className="flex items-center">
                <div className={`bg-gradient-to-r ${stat.gradient} p-2 sm:p-3 rounded-lg`}>
                  <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
              <div className="mt-3 sm:mt-4">
                <p className={`text-xs ${stat.trend === 'up' ? 'text-green-600' : 'text-blue-600'}`}>
                  {stat.change}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
            <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-accent mr-2" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.link}
                className={`${action.bgColor} ${action.hoverColor} rounded-xl p-4 sm:p-6 border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 group`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <action.icon className={`w-6 h-6 sm:w-8 sm:h-8 text-${action.color} mb-2 sm:mb-3`} />
                    <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base">{action.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">{action.description}</p>
                  </div>
                  <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Recent Tickets */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <TicketIconSolid className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-primary mr-2" />
                  Recent Tickets
                </h2>
                <Link
                  to="/support/tickets"
                  className="text-apollo-primary hover:text-apollo-secondary font-medium text-xs sm:text-sm flex items-center"
                >
                  View All
                  <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                </Link>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                {recentTickets.map((ticket) => (
                  <div key={ticket.id} className="border border-gray-100 rounded-lg p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 mb-2">
                          <span className="text-xs sm:text-sm font-mono text-gray-500">{ticket.id}</span>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                              {ticket.priority}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                              {ticket.status}
                            </span>
                          </div>
                        </div>
                        <h3 className="font-medium text-gray-900 mb-1 text-sm sm:text-base">{ticket.title}</h3>
                        <p className="text-xs sm:text-sm text-gray-600 mb-2">{ticket.description}</p>
                        <div className="flex flex-col sm:flex-row sm:items-center text-xs text-gray-500 space-y-1 sm:space-y-0 sm:space-x-4">
                          <span>Created {ticket.created}</span>
                          <span className="hidden sm:inline">•</span>
                          <span>Assigned to {ticket.assignedTo}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Popular Knowledge Base Articles */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <DocumentTextIcon className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-accent mr-2" />
                  Popular Articles
                </h2>
                <Link
                  to="/support/knowledge-base"
                  className="text-apollo-primary hover:text-apollo-secondary font-medium text-xs sm:text-sm flex items-center"
                >
                  Browse All
                  <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                </Link>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                {popularArticles.map((article) => (
                  <div key={article.id} className="border border-gray-100 rounded-lg p-3 sm:p-4 hover:bg-gray-50 transition-colors cursor-pointer">
                    <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">{article.title}</h3>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-gray-600 space-y-2 sm:space-y-0">
                      <span className="bg-apollo-accent/10 text-apollo-accent px-2 py-1 rounded-full text-xs w-fit">
                        {article.category}
                      </span>
                      <div className="flex items-center space-x-3 sm:space-x-4">
                        <span>{article.views} views</span>
                        <div className="flex items-center">
                          <HeartIcon className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 mr-1" />
                          <span>{article.helpful}%</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Updated {article.lastUpdated}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-6 sm:mt-8 bg-gradient-to-r from-apollo-primary to-apollo-accent rounded-xl text-white p-6 sm:p-8">
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Need Immediate Help?</h2>
            <p className="text-apollo-secondary/20 mb-4 sm:mb-6 text-sm sm:text-base">
              Our support team is available 24/7 to assist you with urgent matters
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8">
              <div className="flex items-center">
                <PhoneIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                <div>
                  <p className="font-medium text-sm sm:text-base">Emergency Hotline</p>
                  <p className="text-apollo-secondary/20 text-xs sm:text-sm">+1-800-APOLLO</p>
                </div>
              </div>
              <div className="flex items-center">
                <EnvelopeIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                <div>
                  <p className="font-medium text-sm sm:text-base">Email Support</p>
                  <p className="text-apollo-secondary/20 text-xs sm:text-sm">support@apollo.health</p>
                </div>
              </div>
              <div className="flex items-center">
                <ChatBubbleLeftRightIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                <div>
                  <p className="font-medium text-sm sm:text-base">Live Chat</p>
                  <p className="text-apollo-secondary/20 text-xs sm:text-sm">Available 24/7</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </>
  );
};

export default SupportDashboard;
